import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import type { CategoryImage } from "./CategoryShowcase/ImageWithOverlay";
import type { HeroBannerData } from "./data/product";

type HeroSlideCategoria = CategoryImage & {
    type: "category";
};

type HeroSlide = HeroBannerData | HeroSlideCategoria;

interface HeroCarouselProps {
    slides: HeroSlide[];
}

type PosicionMiniatura = {
    x: number;
    y: number;
    width: number;
    height: number;
    rotate: number;
};

type MedidasHero = {
    width: number;
    height: number;
};

const ALTO_TARJETA_CENTRAL = {
    desktop: 520,
    mobile: 420,
};

const ANCHO_TARJETA_CENTRAL = {
    desktop: 820,
    mobile: 320,
};

const ESTILO_CTA_PRIMARIO: CSSProperties = {
    background: "#2E2114",
    color: "#F5E6C3",
    border: "none",
    borderRadius: 999,
    padding: "14px 22px",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "\"DM Sans\", sans-serif",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    letterSpacing: "0.01em",
    textDecoration: "none",
};

const TRANSICION_SLIDE = {
    type: "spring" as const,
    stiffness: 74,
    damping: 21,
    mass: 1.12,
    restDelta: 0.001,
};

function obtenerPosicionesMiniaturas(cantidad: number, esMobile: boolean, medidas: MedidasHero): PosicionMiniatura[] {
    if (esMobile) {
        if (cantidad === 1) {
            return [
                { x: medidas.width - 138, y: medidas.height * 0.34, width: 128, height: 180, rotate: 5 },
            ];
        }

        if (cantidad === 2) {
            return [
                { x: medidas.width - 128, y: medidas.height * 0.27, width: 118, height: 164, rotate: 5 },
                { x: 10, y: medidas.height * 0.58, width: 118, height: 164, rotate: -5 },
            ];
        }

        if (cantidad === 3) {
            return [
                { x: medidas.width - 128, y: medidas.height * 0.24, width: 118, height: 164, rotate: 5 },
                { x: medidas.width - 118, y: medidas.height * 0.60, width: 104, height: 124, rotate: -4 },
                { x: 8, y: medidas.height * 0.58, width: 118, height: 164, rotate: -5 },
            ];
        }

        return [
            { x: medidas.width - 128, y: medidas.height * 0.24, width: 118, height: 164, rotate: 5 },
            { x: medidas.width - 118, y: medidas.height * 0.60, width: 104, height: 124, rotate: -4 },
            { x: 14, y: medidas.height * 0.60, width: 104, height: 124, rotate: 4 },
            { x: 8, y: medidas.height * 0.24, width: 118, height: 164, rotate: -5 },
        ];
    }

    if (cantidad === 1) {
        return [
            { x: medidas.width - 262, y: medidas.height * 0.19, width: 250, height: 310, rotate: 3 },
        ];
    }

    if (cantidad === 2) {
        return [
            { x: medidas.width - 262, y: medidas.height * 0.19, width: 250, height: 310, rotate: 3 },
            { x: 16, y: medidas.height * 0.20, width: 250, height: 310, rotate: 0 },
        ];
    }

    if (cantidad === 3) {
        return [
            { x: medidas.width - 262, y: medidas.height * 0.19, width: 250, height: 310, rotate: 3 },
            { x: medidas.width - 232, y: medidas.height * 0.61, width: 220, height: 170, rotate: -3 },
            { x: 16, y: medidas.height * 0.20, width: 250, height: 310, rotate: 0 },
        ];
    }

    return [
        { x: medidas.width - 262, y: medidas.height * 0.19, width: 250, height: 310, rotate: 3 },
        { x: medidas.width - 232, y: medidas.height * 0.61, width: 220, height: 170, rotate: -3 },
        { x: 20, y: medidas.height * 0.61, width: 220, height: 170, rotate: 4 },
        { x: 16, y: medidas.height * 0.20, width: 250, height: 310, rotate: 0 },
    ];
}

function obtenerPosicionCentral(esMobile: boolean, medidas: MedidasHero): PosicionMiniatura {
    const width = esMobile
        ? Math.min(ANCHO_TARJETA_CENTRAL.mobile, Math.max(260, medidas.width - 92))
        : Math.min(ANCHO_TARJETA_CENTRAL.desktop, Math.max(320, medidas.width - 380));
    const height = esMobile ? ALTO_TARJETA_CENTRAL.mobile : ALTO_TARJETA_CENTRAL.desktop;

    return {
        x: (medidas.width - width) / 2,
        y: esMobile ? 78 : 120,
        width,
        height,
        rotate: 0,
    };
}

function obtenerSlideKey(slide: HeroSlide, index: number) {
    return slide.type === "banner" ? "banner" : `${slide.link}-${index}`;
}

function FondoHero() {
    return (
        <>
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(120% 90% at 20% 30%, #F1DDA8 0%, transparent 55%), radial-gradient(80% 70% at 85% 80%, #E8B95C 0%, transparent 60%), linear-gradient(180deg, #F6ECD4 0%, #EDDEB7 100%)",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    left: "6%",
                    top: "46%",
                    width: 320,
                    height: 320,
                    borderRadius: "50%",
                    background: "radial-gradient(circle at 35% 30%, #C9863A, #8A5A2E 60%, transparent 75%)",
                    filter: "blur(4px)",
                    opacity: 0.48,
                }}
            />
            <div
                style={{
                    position: "absolute",
                    right: "4%",
                    top: "6%",
                    width: 280,
                    height: 280,
                    borderRadius: "50%",
                    background: "radial-gradient(circle at 50% 50%, #F5E6C3, transparent 70%)",
                    opacity: 0.85,
                }}
            />
        </>
    );
}

function PanelVidrio({ children, style = {} }: { children: React.ReactNode; style?: CSSProperties }) {
    return (
        <div
            style={{
                position: "relative",
                background: "linear-gradient(180deg, rgba(255,250,238,0.72) 0%, rgba(248,236,208,0.55) 100%)",
                border: "1px solid rgba(255,250,238,0.7)",
                boxShadow:
                    "0 30px 60px -20px rgba(70,42,12,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(120,80,30,0.08)",
                backdropFilter: "blur(28px) saturate(160%)",
                WebkitBackdropFilter: "blur(28px) saturate(160%)",
                borderRadius: 32,
                overflow: "hidden",
                ...style,
            }}
        >
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    pointerEvents: "none",
                    background: "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 35%)",
                    mixBlendMode: "screen",
                }}
            />
            {children}
        </div>
    );
}

function FotoCategoria({
    src,
    alt,
    titulo,
    pequena = false,
    tono = "honey",
}: {
    src?: string;
    alt: string;
    titulo: string;
    pequena?: boolean;
    tono?: "honey" | "cocoa" | "cream" | "sage" | "intro";
}) {
    const paletas = {
        honey: { from: "#EBC472", to: "#A96A2C" },
        cocoa: { from: "#8E5C2F", to: "#3F2614" },
        cream: { from: "#F1DDA8", to: "#C98E45" },
        sage: { from: "#B7BA8C", to: "#6E7A4A" },
        intro: { from: "#F1DDA8", to: "#D29A4A" },
    };

    const paleta = paletas[tono];

    return (
        <div
            style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(135deg, ${paleta.from} 0%, ${paleta.to} 100%)`,
            }}
        >
            {src ? (
                <img
                    src={src}
                    alt={alt}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                    }}
                />
            ) : null}

            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background: "radial-gradient(60% 50% at 50% 45%, rgba(255,240,210,0.32) 0%, transparent 70%)",
                }}
            />

            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.32,
                    backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(255,255,255,0.07) 0 3px, transparent 3px 12px)",
                }}
            />

            <div
                style={{
                    position: "absolute",
                    top: pequena ? 12 : 22,
                    left: pequena ? 12 : 22,
                    fontFamily: "ui-monospace, \"JetBrains Mono\", monospace",
                    fontSize: pequena ? 9 : 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "rgba(255,248,230,0.78)",
                }}
            >
                {titulo}
            </div>
        </div>
    );
}

function SlideBannerEditorial({ slide, esMobile }: { slide: HeroBannerData; esMobile: boolean }) {
    return (
        <PanelVidrio
            style={{
                width: "100%",
                height: "100%",
                padding: esMobile ? "38px 24px 28px" : "60px 64px 48px",
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: esMobile ? 8 : 10,
                    fontSize: esMobile ? 10 : 11,
                    letterSpacing: "0.36em",
                    textTransform: "uppercase",
                    color: "#8A5A2E",
                    marginBottom: esMobile ? 18 : 22,
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <span>✦</span>
                <span>{slide.eyebrow}</span>
                <span>✦</span>
            </div>

            <h1
                style={{
                    fontFamily: "\"Cormorant Garamond\", serif",
                    fontWeight: 300,
                    fontSize: esMobile ? 44 : 86,
                    lineHeight: esMobile ? 0.98 : 0.97,
                    letterSpacing: "-0.012em",
                    textAlign: "center",
                    margin: "0 0 20px",
                    color: "#2E2114",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                {slide.headlineBeforeAccent}{" "}
                <em style={{ fontStyle: "italic", color: "#8A5A2E", fontWeight: 400 }}>{slide.headlineAccent}</em>{" "}
                {slide.headlineAfterAccent}
            </h1>

            <p
                style={{
                    textAlign: "center",
                    maxWidth: 560,
                    margin: esMobile ? "0 auto 28px" : "0 auto 38px",
                    fontSize: esMobile ? 14 : 15.5,
                    lineHeight: 1.55,
                    color: "#5E4A30",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                {slide.body}
            </p>

            <div
                style={{
                    display: "flex",
                    gap: 12,
                    justifyContent: "center",
                    flexWrap: "wrap",
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <Link to={slide.primaryCtaHref} style={ESTILO_CTA_PRIMARIO}>
                    {slide.primaryCtaText}
                </Link>
            </div>
        </PanelVidrio>
    );
}

function SlideCategoriaCentral({ slide, esMobile }: { slide: HeroSlideCategoria; esMobile: boolean }) {
    return (
        <Link
            to={slide.link}
            style={{
                width: "100%",
                height: "100%",
                position: "relative",
                borderRadius: 32,
                overflow: "hidden",
                isolation: "isolate",
                boxShadow: "0 30px 60px -20px rgba(70,42,12,0.4), inset 0 1px 0 rgba(255,255,255,0.4)",
                border: "1px solid rgba(255,250,238,0.5)",
                boxSizing: "border-box",
                display: "block",
                textDecoration: "none",
            }}
        >
            <FotoCategoria src={slide.src} alt={slide.alt} titulo={slide.title} />

            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: "linear-gradient(180deg, transparent 55%, rgba(20,12,0,0.55) 100%)",
                }}
            />

            <div
                style={{
                    position: "absolute",
                    left: "50%",
                    bottom: esMobile ? 28 : 48,
                    transform: "translateX(-50%)",
                    background: "rgba(255,250,238,0.85)",
                    color: "#2E2114",
                    border: "1px solid rgba(255,250,238,0.7)",
                    backdropFilter: "blur(20px) saturate(160%)",
                    WebkitBackdropFilter: "blur(20px) saturate(160%)",
                    borderRadius: 99,
                    padding: esMobile ? "14px 22px" : "16px 32px",
                    fontFamily: "\"DM Sans\", sans-serif",
                    fontSize: esMobile ? 12 : 14,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                    boxShadow: "0 14px 28px -10px rgba(0,0,0,0.35)",
                    whiteSpace: "nowrap",
                }}
            >
                {slide.title}
                <span style={{ fontSize: esMobile ? 12 : 14 }}>↗</span>
            </div>
        </Link>
    );
}

function TarjetaCentral({ slide, esMobile }: { slide: HeroSlide; esMobile: boolean }) {
    if (slide.type === "banner") {
        return <SlideBannerEditorial slide={slide} esMobile={esMobile} />;
    }

    return <SlideCategoriaCentral slide={slide} esMobile={esMobile} />;
}

function MiniaturaBanner({ slide }: { slide: HeroBannerData }) {
    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                borderRadius: 22,
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 18px 30px -14px rgba(60,32,8,0.45)",
                border: "1px solid rgba(255,250,238,0.4)",
                background:
                    "radial-gradient(120% 90% at 20% 30%, #F1DDA8 0%, transparent 55%), radial-gradient(80% 70% at 85% 80%, #E8B95C 0%, transparent 60%), linear-gradient(180deg, #F6ECD4 0%, #EDDEB7 100%)",
            }}
        >
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 40%)",
                }}
            />

            <div
                style={{
                    position: "absolute",
                    inset: 18,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                    textAlign: "center",
                    fontFamily: "\"DM Sans\", sans-serif",
                    fontSize: 9,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: "#2E2114",
                    lineHeight: 1.5,
                    textShadow: "0 2px 10px rgba(255,255,255,0.55)",
                }}
            >
                <span aria-hidden="true">{"\u2726"}</span>
                <span style={{ whiteSpace: "nowrap" }}>{slide.eyebrow}</span>
                <span aria-hidden="true">{"\u2726"}</span>
            </div>
        </div>
    );
}

function MiniaturaCategoria({ slide }: { slide: HeroSlideCategoria }) {
    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                borderRadius: 22,
                overflow: "hidden",
                position: "relative",
                boxShadow: "0 18px 30px -14px rgba(60,32,8,0.45)",
                border: "1px solid rgba(255,250,238,0.4)",
            }}
        >
            <FotoCategoria src={slide.src} alt={slide.alt} titulo={slide.title} pequena />

            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background: "linear-gradient(180deg, transparent 50%, rgba(20,12,0,0.6) 100%)",
                }}
            />

            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 14,
                    textAlign: "center",
                    fontFamily: "\"DM Sans\", sans-serif",
                    fontSize: 12,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    color: "#F5E6C3",
                    textShadow: "0 2px 8px rgba(0,0,0,0.6)",
                    padding: "0 10px",
                }}
            >
                {slide.title}
            </div>
        </div>
    );
}

function MiniaturaSlide({ slide }: { slide: HeroSlide }) {
    if (slide.type === "banner") {
        return <MiniaturaBanner slide={slide} />;
    }

    return <MiniaturaCategoria slide={slide} />;
}

function FlechaCarrusel({
    lado,
    onClick,
    esMobile,
}: {
    lado: "left" | "right";
    onClick: () => void;
    esMobile: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={lado === "left" ? "Slide anterior" : "Slide siguiente"}
            style={{
                position: "absolute",
                [lado]: esMobile ? 10 : 18,
                top: "50%",
                transform: "translateY(-50%)",
                width: esMobile ? 42 : 52,
                height: esMobile ? 42 : 52,
                borderRadius: 999,
                background: "rgba(255,250,238,0.65)",
                border: "1px solid rgba(120,80,30,0.14)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#2E2114",
                zIndex: 8,
                boxShadow: "0 10px 24px -10px rgba(70,42,12,0.3)",
                cursor: "pointer",
                fontSize: esMobile ? 16 : 18,
                transition: "transform 0.2s ease, background 0.2s ease",
            }}
        >
            {lado === "left" ? "←" : "→"}
        </button>
    );
}

function Puntos({
    active,
    total,
    jumpTo,
}: {
    active: number;
    total: number;
    jumpTo: (idx: number) => void;
}) {
    return (
        <div
            style={{
                position: "absolute",
                bottom: 18,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
                zIndex: 8,
            }}
        >
            {Array.from({ length: total }).map((_, index) => (
                <button
                    key={index}
                    type="button"
                    onClick={() => jumpTo(index)}
                    aria-label={`Ir a slide ${index + 1}`}
                    style={{
                        width: index === active ? 26 : 8,
                        height: 8,
                        borderRadius: 4,
                        background: index === active ? "#2E2114" : "rgba(46,33,20,0.25)",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        transition: "width 0.35s ease, background 0.25s ease",
                    }}
                />
            ))}
        </div>
    );
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
    const esMobile = useMediaQuery("(max-width:900px)");
    const slidesVisibles = useMemo(() => slides.slice(0, 5), [slides]);
    const [indiceActivo, setIndiceActivo] = useState(0);
    const [indiceEnSalida, setIndiceEnSalida] = useState<number | null>(null);
    const [indiceEnEntrada, setIndiceEnEntrada] = useState<number | null>(null);
    const [medidasHero, setMedidasHero] = useState<MedidasHero>({ width: 0, height: 0 });
    const [pausarAutoplay, setPausarAutoplay] = useState(false);
    const refHero = useRef<HTMLDivElement | null>(null);
    const refTimeoutSalida = useRef<number | null>(null);
    const refTimeoutEntrada = useRef<number | null>(null);
    const altoHero = esMobile ? 560 : 760;

    useEffect(() => {
        if (slidesVisibles.length === 0) {
            setIndiceActivo(0);
            return;
        }

        setIndiceActivo((prev) => (prev >= slidesVisibles.length ? 0 : prev));
    }, [slidesVisibles.length]);

    const marcarTransicion = useCallback((indexSalida: number, indexEntrada: number) => {
        setIndiceEnSalida(indexSalida);
        setIndiceEnEntrada(indexEntrada);

        if (refTimeoutSalida.current !== null) {
            window.clearTimeout(refTimeoutSalida.current);
        }

        if (refTimeoutEntrada.current !== null) {
            window.clearTimeout(refTimeoutEntrada.current);
        }

        refTimeoutSalida.current = window.setTimeout(() => {
            setIndiceEnSalida(null);
            refTimeoutSalida.current = null;
        }, 950);

        refTimeoutEntrada.current = window.setTimeout(() => {
            setIndiceEnEntrada(null);
            refTimeoutEntrada.current = null;
        }, 880);
    }, []);

    const ir = useCallback(
        (delta: number) => {
            if (slidesVisibles.length <= 1) return;

            setIndiceActivo((actual) => {
                const siguiente = (actual + delta + slidesVisibles.length) % slidesVisibles.length;
                if (siguiente !== actual) {
                    marcarTransicion(actual, siguiente);
                }

                return siguiente;
            });
        },
        [marcarTransicion, slidesVisibles.length]
    );

    const saltarA = useCallback(
        (index: number) => {
            setIndiceActivo((actual) => {
                if (index === actual) return actual;
                marcarTransicion(actual, index);
                return index;
            });
        },
        [marcarTransicion]
    );

    useEffect(() => {
        if (slidesVisibles.length <= 1 || pausarAutoplay) return;

        const intervalo = window.setInterval(() => {
            ir(1);
        }, 6500);

        return () => window.clearInterval(intervalo);
    }, [ir, pausarAutoplay, slidesVisibles.length]);

    useEffect(() => {
        const nodo = refHero.current;
        if (!nodo) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowRight") {
                ir(1);
            } else if (event.key === "ArrowLeft") {
                ir(-1);
            }
        };

        nodo.addEventListener("keydown", onKeyDown);
        return () => nodo.removeEventListener("keydown", onKeyDown);
    }, [ir]);

    useEffect(() => {
        const nodo = refHero.current;
        if (!nodo) return;

        const actualizarMedidas = () => {
            setMedidasHero({
                width: nodo.clientWidth,
                height: nodo.clientHeight || altoHero,
            });
        };

        actualizarMedidas();

        if (typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const observer = new ResizeObserver((entries) => {
            const rect = entries[0]?.contentRect;
            if (!rect) return;

            setMedidasHero({
                width: rect.width,
                height: rect.height || altoHero,
            });
        });

        observer.observe(nodo);
        return () => observer.disconnect();
    }, [altoHero]);

    useEffect(() => {
        return () => {
            if (refTimeoutSalida.current !== null) {
                window.clearTimeout(refTimeoutSalida.current);
            }

            if (refTimeoutEntrada.current !== null) {
                window.clearTimeout(refTimeoutEntrada.current);
            }
        };
    }, []);

    if (slidesVisibles.length === 0) {
        return null;
    }

    const medidasCalculadas: MedidasHero = {
        width: medidasHero.width || (esMobile ? 390 : 1280),
        height: medidasHero.height || altoHero,
    };
    const posicionCentral = obtenerPosicionCentral(esMobile, medidasCalculadas);
    const posicionesMiniaturas = obtenerPosicionesMiniaturas(
        Math.max(slidesVisibles.length - 1, 0),
        esMobile,
        medidasCalculadas
    );

    return (
        <section
            style={{
                padding: esMobile ? "16px 12px 32px" : "28px 32px 48px",
                position: "relative",
                zIndex: 2,
            }}
        >
            <div
                ref={refHero}
                tabIndex={0}
                onMouseEnter={() => setPausarAutoplay(true)}
                onMouseLeave={() => setPausarAutoplay(false)}
                style={{
                    position: "relative",
                    height: altoHero,
                    overflow: "hidden",
                    outline: "none",
                    borderRadius: esMobile ? 28 : 40,
                    boxShadow: "0 40px 100px -40px rgba(60,40,20,0.28)",
                }}
            >
                <style>{`
                    .sem-hero-slide-motion {
                        will-change: transform, width, height;
                        transform-origin: center center;
                    }

                    .sem-hero-slide-motion:hover {
                        filter: brightness(1.06);
                    }
                `}</style>

                <FondoHero />

                {slidesVisibles.map((slide, index) => {
                    const esActiva = index === indiceActivo;
                    const estaSaliendo = index === indiceEnSalida && !esActiva;
                    const estaEntrandoComoBanner = esActiva && slide.type === "banner" && index === indiceEnEntrada;
                    const offset = (index - indiceActivo + slidesVisibles.length) % slidesVisibles.length;
                    const posicion = esActiva ? posicionCentral : posicionesMiniaturas[offset - 1];

                    if (!posicion) {
                        return null;
                    }

                    return (
                        <motion.div
                            key={obtenerSlideKey(slide, index)}
                            className="sem-hero-slide-motion"
                            initial={false}
                            animate={{
                                x: posicion.x,
                                y: posicion.y,
                                width: posicion.width,
                                height: posicion.height,
                                rotate: posicion.rotate,
                                opacity: 1,
                            }}
                            transition={TRANSICION_SLIDE}
                            style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                zIndex: esActiva ? 6 : estaSaliendo ? 7 : 2 + (slidesVisibles.length - offset),
                                pointerEvents: "auto",
                            }}
                        >
                            {esActiva ? (
                                estaEntrandoComoBanner ? (
                                    <MiniaturaSlide slide={slide} />
                                ) : (
                                    <motion.div
                                        key="contenido-central"
                                        initial={slide.type === "banner" ? { opacity: 0 } : false}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.22, ease: "easeOut" }}
                                        style={{ width: "100%", height: "100%" }}
                                    >
                                        <TarjetaCentral slide={slide} esMobile={esMobile} />
                                    </motion.div>
                                )
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => saltarA(index)}
                                    aria-label={slide.type === "banner" ? "Ir al banner principal" : `Ir a ${slide.title}`}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        background: "transparent",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                    }}
                                >
                                    <MiniaturaSlide slide={slide} />
                                </button>
                            )}
                        </motion.div>
                    );
                })}

                {slidesVisibles.length > 1 ? (
                    <>
                        <FlechaCarrusel lado="left" onClick={() => ir(-1)} esMobile={esMobile} />
                        <FlechaCarrusel lado="right" onClick={() => ir(1)} esMobile={esMobile} />
                        <Puntos active={indiceActivo} total={slidesVisibles.length} jumpTo={saltarA} />
                    </>
                ) : null}
            </div>
        </section>
    );
}
